import { useCallback, useEffect, useMemo } from "react"

import { useRecoilState, useRecoilValue } from "recoil"
import { v4 as uuidv4 } from "uuid"

import { pluginStudioAtom } from "../atoms/pluginStudioAtom"
import { activePluginSelector } from "../selectors"
import {
  EventData,
  getCodeEditorPluginId,
  LiveData,
  PluginConfigData,
  setLivePreviewPluginId,
  TestCase,
  TestCaseUser,
  UserRole,
} from "../types"

/**
 * Hook for managing test data in the plugin studio
 */
export function useTestData() {
  const [state, setState] = useRecoilState(pluginStudioAtom)
  const activePlugin = useRecoilValue(activePluginSelector)

  // Get derived values
  const testCases = state.testCases
  const activeTestCaseId = state.activeTestCaseId
  const activeTestCase = useMemo(
    () => state.testCases.find((tc) => tc.id === activeTestCaseId) || null,
    [state.testCases, activeTestCaseId],
  )

  // Live data
  const liveData = state.liveData
  const activeLiveUser = useMemo(
    () =>
      liveData.users.find((user) => user.id === liveData.activeUserId) || null,
    [liveData.users, liveData.activeUserId],
  )

  /**
   * Create a new test case
   * @returns The ID of the new test case
   */
  const createTestCase = useCallback(() => {
    const newTestCaseId = uuidv4()

    setState((currentState) => {
      // Create a new test case
      const newTestCase: TestCase = {
        id: newTestCaseId,
        name: `Test Case ${currentState.testCases.length + 1}`,
        eventData: {
          id: uuidv4(),
          title: "Test Event",
          description: "A test event for plugin testing",
          date: new Date().toISOString(),
          location: {
            name: "Test Location",
            address: "123 Test St, Anywhere",
          },
          flyerImage: "",
        },
        pluginConfig: {
          enabled: true,
          settings: {},
        },
        eventPluginData: {},
        settings: {
          triggerDelay: 0,
          simulateNetworkDelay: false,
        },
      }

      return {
        ...currentState,
        testCases: [...currentState.testCases, newTestCase],
        activeTestCaseId: newTestCaseId,
      }
    })

    return newTestCaseId
  }, [setState])

  /**
   * Update a test case
   * @param testCaseId The ID of the test case to update
   * @param updates Partial updates to apply to the test case
   */
  const updateTestCase = useCallback(
    (testCaseId: string, updates: Partial<TestCase>) => {
      setState((currentState) => ({
        ...currentState,
        testCases: currentState.testCases.map((testCase) =>
          testCase.id === testCaseId ? { ...testCase, ...updates } : testCase,
        ),
      }))
    },
    [setState],
  )

  /**
   * Delete a test case
   * @param testCaseId The ID of the test case to delete
   */
  const deleteTestCase = useCallback(
    (testCaseId: string) => {
      setState((currentState) => {
        // If we're deleting the active test case, set a new active test case
        if (currentState.testCases.length <= 1) {
          return currentState
        }

        const newTestCases = currentState.testCases.filter(
          (testCase) => testCase.id !== testCaseId,
        )

        let newActiveTestCaseId = currentState.activeTestCaseId
        if (currentState.activeTestCaseId === testCaseId) {
          newActiveTestCaseId = newTestCases[0]?.id || null
        }

        return {
          ...currentState,
          testCases: newTestCases,
          activeTestCaseId: newActiveTestCaseId,
        }
      })
    },
    [setState],
  )

  /**
   * Select a test case as active
   * @param testCaseId The ID of the test case to select
   */
  const selectTestCase = useCallback(
    (testCaseId: string) => {
      setState((currentState) => ({
        ...currentState,
        activeTestCaseId: testCaseId,
      }))
    },
    [setState],
  )

  /**
   * Load a test case into live data
   */
  const loadTestCaseToLive = useCallback(
    (testCaseId: string) => {
      setState((currentState) => {
        const testCase = currentState.testCases.find(
          (tc) => tc.id === testCaseId,
        )
        if (!testCase) return currentState

        // Create default users
        const guestUserId = uuidv4()
        const hostUserId = uuidv4()

        const defaultUsers: TestCaseUser[] = [
          {
            id: guestUserId,
            name: "Guest User",
            role: UserRole.GUEST,
            userData: {
              id: guestUserId,
              displayName: "Guest User",
              role: UserRole.GUEST,
            },
            userPluginData: {},
          },
          {
            id: hostUserId,
            name: "Host User",
            role: UserRole.HOST_EVENT_VIEW,
            userData: {
              id: hostUserId,
              displayName: "Host User",
              role: UserRole.HOST_EVENT_VIEW,
            },
            userPluginData: {},
          },
        ]

        return {
          ...currentState,
          activeTestCaseId: testCaseId,
          liveData: {
            sourceTestCaseId: testCaseId,
            eventData: { ...testCase.eventData },
            pluginConfig: { ...testCase.pluginConfig },
            eventPluginData: { ...testCase.eventPluginData },
            users: defaultUsers,
            activeUserId: defaultUsers[0].id,
            livePreviewPluginId: currentState.liveData.livePreviewPluginId,
            isDirty: false,
          },
        }
      })
    },
    [setState],
  )

  /**
   * Update the event data in live data
   * @param updates Partial updates to apply to the event data
   */
  const updateLiveEventData = useCallback(
    (updates: Partial<EventData>) => {
      setState((currentState) => ({
        ...currentState,
        liveData: {
          ...currentState.liveData,
          eventData: {
            ...currentState.liveData.eventData,
            ...updates,
          },
          isDirty: true,
        },
      }))
    },
    [setState],
  )

  /**
   * Update the plugin configuration in live data
   * @param updates Partial updates to apply to the plugin configuration
   */
  const updateLivePluginConfig = useCallback(
    (updates: Partial<PluginConfigData>) => {
      setState((currentState) => ({
        ...currentState,
        liveData: {
          ...currentState.liveData,
          pluginConfig: {
            ...currentState.liveData.pluginConfig,
            ...updates,
          },
          isDirty: true,
        },
      }))
    },
    [setState],
  )

  /**
   * Set the active user in live data
   * @param userId The ID of the user to set as active
   */
  const setLiveActiveUser = useCallback(
    (userId: string) => {
      setState((currentState) => ({
        ...currentState,
        liveData: {
          ...currentState.liveData,
          activeUserId: userId,
        },
      }))
    },
    [setState],
  )

  /**
   * Reset live data from the current test case
   */
  const resetLiveDataFromTestCase = useCallback(() => {
    setState((currentState) => {
      const testCaseId = currentState.liveData.sourceTestCaseId
      if (!testCaseId) return currentState

      const testCase = currentState.testCases.find((tc) => tc.id === testCaseId)
      if (!testCase) return currentState

      // Create default users
      const guestUserId = uuidv4()
      const hostUserId = uuidv4()

      const defaultUsers: TestCaseUser[] = [
        {
          id: guestUserId,
          name: "Guest User",
          role: UserRole.GUEST,
          userData: {
            id: guestUserId,
            displayName: "Guest User",
            role: UserRole.GUEST,
          },
          userPluginData: {},
        },
        {
          id: hostUserId,
          name: "Host User",
          role: UserRole.HOST_EVENT_VIEW,
          userData: {
            id: hostUserId,
            displayName: "Host User",
            role: UserRole.HOST_EVENT_VIEW,
          },
          userPluginData: {},
        },
      ]

      return {
        ...currentState,
        liveData: {
          sourceTestCaseId: testCaseId,
          eventData: { ...testCase.eventData },
          pluginConfig: { ...testCase.pluginConfig },
          eventPluginData: { ...testCase.eventPluginData },
          users: defaultUsers,
          activeUserId: defaultUsers[0].id,
          livePreviewPluginId: currentState.liveData.livePreviewPluginId,
          isDirty: false,
        },
      }
    })
  }, [setState])

  /**
   * Sync current live data back to the source test case
   */
  const syncLiveDataToTestCase = useCallback(() => {
    setState((currentState) => {
      const testCaseId = currentState.liveData.sourceTestCaseId
      if (!testCaseId) return currentState

      const testCaseIndex = currentState.testCases.findIndex(
        (tc) => tc.id === testCaseId,
      )
      if (testCaseIndex === -1) return currentState

      // Create a new test cases array with the updated test case
      const newTestCases = [...currentState.testCases]
      newTestCases[testCaseIndex] = {
        ...newTestCases[testCaseIndex],
        eventData: { ...currentState.liveData.eventData },
        pluginConfig: { ...currentState.liveData.pluginConfig },
        eventPluginData: { ...currentState.liveData.eventPluginData },
      }

      return {
        ...currentState,
        testCases: newTestCases,
        liveData: {
          ...currentState.liveData,
          isDirty: false,
        },
      }
    })
  }, [setState])

  /**
   * Initialize a test case if none exists
   */
  useEffect(() => {
    if (state.testCases.length === 0) {
      createTestCase()
    } else if (!state.activeTestCaseId) {
      selectTestCase(state.testCases[0].id)
    }
  }, [state.testCases.length, state.activeTestCaseId])

  /**
   * Initialize live data with an active plugin
   */
  useEffect(() => {
    if (!state.liveData.livePreviewPluginId && activePlugin) {
      // Set the live active plugin ID to match the studio active plugin ID
      setState((currentState) => {
        // Use the studio active plugin ID or the first plugin in the list
        const codeEditorPluginId = getCodeEditorPluginId(currentState)
        const pluginId = codeEditorPluginId || currentState.plugins[0]?.id

        if (!pluginId) return currentState

        // Use the setter function to update only the live active plugin ID
        return setLivePreviewPluginId(currentState, pluginId)
      })
    }
  }, [activePlugin, state.liveData.livePreviewPluginId])

  /**
   * Add a new user to live data
   */
  const addLiveUser = useCallback(
    (role: UserRole) => {
      const userId = `user-${uuidv4()}`
      const newUser: TestCaseUser = {
        id: userId,
        name: `${role} User`,
        role: role,
        viewAs: role,
        userData: {
          displayName: `${role} User`,
          role: role,
        },
        userPluginData: {},
      }

      // Update live data with new user
      setState((currentState) => ({
        ...currentState,
        liveData: {
          ...currentState.liveData,
          users: [...currentState.liveData.users, newUser],
          isDirty: true,
        },
      }))

      return userId
    },
    [setState],
  )

  /**
   * Remove a user from live data
   */
  const removeLiveUser = useCallback(
    (userId: string) => {
      setState((currentState) => {
        // Don't remove if it's the last user
        if (currentState.liveData.users.length <= 1) {
          return currentState
        }

        const newUsers = currentState.liveData.users.filter(
          (user) => user.id !== userId,
        )

        // If we're removing the active user, select another one
        let newActiveUserId = currentState.liveData.activeUserId
        if (userId === currentState.liveData.activeUserId) {
          newActiveUserId = newUsers[0].id
        }

        return {
          ...currentState,
          liveData: {
            ...currentState.liveData,
            users: newUsers,
            activeUserId: newActiveUserId,
            isDirty: true,
          },
        }
      })
    },
    [setState],
  )

  /**
   * Update a user in live data
   */
  const updateLiveUser = useCallback(
    (userId: string, updates: Partial<TestCaseUser>) => {
      setState((currentState) => {
        const userIndex = currentState.liveData.users.findIndex(
          (user) => user.id === userId,
        )
        if (userIndex === -1) return currentState

        const newUsers = [...currentState.liveData.users]
        newUsers[userIndex] = {
          ...newUsers[userIndex],
          ...updates,
        }

        return {
          ...currentState,
          liveData: {
            ...currentState.liveData,
            users: newUsers,
            isDirty: true,
          },
        }
      })
    },
    [setState],
  )

  return {
    testCases,
    activeTestCaseId,
    activeTestCase,
    liveData,
    activeLiveUser,
    createTestCase,
    updateTestCase,
    deleteTestCase,
    selectTestCase,
    loadTestCaseToLive,
    updateLiveEventData,
    updateLivePluginConfig,
    setLiveActiveUser,
    resetLiveDataFromTestCase,
    syncLiveDataToTestCase,
    addLiveUser,
    removeLiveUser,
    updateLiveUser,
  }
}
